import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Publiek: is het artikel gepubliceerd + bevestigde kennisbank-antwoorden. */
export const getBlogPublicState = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; keys: string[] }) =>
    z.object({ slug: z.string().max(120), keys: z.array(z.string().max(60)).max(10) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { publicDb } = await import("@/lib/db.server");
    const db = publicDb();
    const [{ data: status }, { data: answers }] = await Promise.all([
      db.from("blog_artikel_status").select("slug").eq("slug", data.slug).eq("status", "gepubliceerd").maybeSingle(),
      data.keys.length
        ? db.from("kennisbank_items").select("key,onderwerp,antwoord").in("key", data.keys).eq("status", "bevestigd")
        : Promise.resolve({ data: [] as { key: string; onderwerp: string; antwoord: string | null }[] }),
    ]);
    return { published: !!status, answers: (answers ?? []).filter((a) => a.antwoord) };
  });

/** Publiek: lijst gepubliceerde slugs (voor overzicht en sitemap). */
export const listPublishedBlogSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const { memo } = await import("@/lib/server-memo");
  return memo("blog-slugs", async () => {
  const { publicDb } = await import("@/lib/db.server");
  const { data } = await publicDb().from("blog_artikel_status").select("slug").eq("status", "gepubliceerd");
  return (data ?? []).map((r) => r.slug);
  });
});

type Ctx = { supabase: any; userId: string };

async function assertAdmin(ctx: Ctx) {
  const { data } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "admin" });
  if (data !== true) throw new Error("Geen beheerdersrechten");
}

export const getBlogAdminData = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as unknown as Ctx;
    await assertAdmin(ctx);
    const [{ data: statuses }, { data: kennis }] = await Promise.all([
      ctx.supabase.from("blog_artikel_status").select("*"),
      ctx.supabase.from("kennisbank_items").select("*").order("onderwerp"),
    ]);
    return {
      statuses: (statuses ?? []) as { slug: string; status: string; notitie: string | null; gepubliceerd_op: string | null }[],
      kennis: (kennis ?? []) as {
        key: string;
        onderwerp: string;
        vraag: string;
        voorstel: string | null;
        antwoord: string | null;
        status: string;
        artikelen: string[];
      }[],
    };
  });

export const setBlogStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string; status: string; notitie?: string }) =>
    z
      .object({
        slug: z.string().max(120),
        status: z.enum(["concept", "wacht_op_informatie", "klaar_voor_controle", "goedgekeurd", "gepubliceerd", "afgewezen"]),
        notitie: z.string().max(2000).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const ctx = context as unknown as Ctx;
    await assertAdmin(ctx);
    const now = new Date().toISOString();
    if (data.status === "gepubliceerd") {
      const { data: cur } = await ctx.supabase
        .from("blog_artikel_status").select("status").eq("slug", data.slug).maybeSingle();
      const s = (cur as { status?: string } | null)?.status;
      if (s !== "goedgekeurd" && s !== "gepubliceerd") {
        throw new Error("Keur het artikel eerst goed voordat je het publiceert.");
      }
    }
    const row: Record<string, unknown> = { slug: data.slug, status: data.status, updated_at: now };
    if (data.notitie !== undefined) row["notitie"] = data.notitie;
    if (data.status === "goedgekeurd") row["goedgekeurd_op"] = now;
    if (data.status === "gepubliceerd") row["gepubliceerd_op"] = now;
    const { error } = await ctx.supabase.from("blog_artikel_status").upsert(row as never);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const saveKennisAntwoord = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string; antwoord: string }) =>
    z.object({ key: z.string().max(60), antwoord: z.string().max(4000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const ctx = context as unknown as Ctx;
    await assertAdmin(ctx);
    const antwoord = data.antwoord.trim();
    const { error } = await ctx.supabase
      .from("kennisbank_items")
      .update({ antwoord: antwoord || null, status: antwoord ? "bevestigd" : "informatie_nodig", updated_at: new Date().toISOString() })
      .eq("key", data.key);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
